'use client';

import React, {useState, useEffect} from 'react';
import Image from 'next/image';
import {
  AWSConstants,
  AWSManager,
  LoadingIndicator,
  SkillzUI,
  _m,
  Trans,
  useTranslation,
} from '@sdk-mobile';
// @ts-ignore
import OtpInputs from 'react-native-otp-inputs';
import IconInfo from '@/../public/icon_info_filled_red.svg';
import RegistrationStep from '@/app/(app)/games/[gid]/sign-in/_src/RegistrationStep';

const {useCountdownNumber, formatCountdown} = SkillzUI;

const AWS_EVENTS = {
  EXIT_PRESS: 'SIGN_IN_EXIT',
  INFO_PRESS: 'SIGN_IN_INFO',
  VIEW_PHONE_TAB: 'SIGN_IN_PHONE_VIEW',
  VIEW_EMAIL_TAB: 'SIGN_IN_EMAIL_VIEW',
  AREA_CODE_BOX_PRESS: 'SIGN_IN_PHONE_AREACODE_TAP',
  SEND_CODE: 'SIGN_IN_PHONE_SEND',
  SEND_CODE_CALL: 'SIGN_IN_PHONE_CALL_SEND',
  ENTER_CODE: 'SIGN_IN_SMS_ENTER',
  CALL_REQUEST: 'ACCOUNT_SMS_VERIFY_CALL_SEND',
};

const NUMBER_OF_INPUTS = 5;

const CALL_ME_COOL_DOWN = 60; // 60 seconds
const RESEND_COOL_DOWN = 60; // 60 seconds
const SECOND_TO_MS = 1000;

type Props = {
  callInsteadEnabled: boolean;
  callMeInsteadPress: () => void;
  countryValue: string;
  editPress: () => void;
  errorStyle: boolean;
  errorMessage?: string;
  gameIcon?: string;
  gameTitle?: string;
  phoneNumber: string;
  resendEnabled: boolean;
  liveEvent?: boolean;
  setCallInsteadEnabled: (enabled: boolean) => void;
  setErrorMessage: (message: string) => void;
  setErrorStyle: (isError: boolean) => void;
  setIsSigningIn: (isSigning: boolean) => void;
  setResendEnabled: (enabled: boolean) => void;
  setShowBanner: (visible: boolean) => void;
  codeValidation: (
    code: string,
    phone: string,
    onSuccess: () => void,
    onFailure: (error: string) => void
  ) => void;
  onSendPressed: () => void;
};

export default function CodeInput({
  callInsteadEnabled,
  callMeInsteadPress,
  countryValue,
  editPress,
  errorStyle,
  errorMessage,
  gameIcon,
  gameTitle,
  phoneNumber,
  resendEnabled,
  liveEvent,
  setCallInsteadEnabled,
  setErrorMessage,
  setErrorStyle,
  setIsSigningIn,
  setResendEnabled,
  setShowBanner,
  codeValidation,
  onSendPressed,
}: Props) {
  const {t} = useTranslation();

  const [resendTimer, setResendTimer] = useState(0); // sets timer for resend code button
  const [callMeTimer, setCallMeTimer] = useState(0); // sets timer for call me instead button

  useEffect(() => {
    LoadingIndicator.enable();
  }, []);

  const handleChange = (otpCode: string) => {
    if (otpCode.length === NUMBER_OF_INPUTS) {
      AWSManager.trackEvent(AWS_EVENTS.ENTER_CODE);
      if (liveEvent) {
        LoadingIndicator.show();
      } else {
        LoadingIndicator.disable();
        setIsSigningIn(true);
      }
      const noSpecialCharacters = phoneNumber.replace(/[^\w ]/g, '').replace(/\s/g, '');
      codeValidation(
        otpCode,
        `${countryValue}${noSpecialCharacters}`,
        () => {},
        (errorString) => {
          setErrorMessage(errorString);
          setErrorStyle(true);
          if (liveEvent) {
            LoadingIndicator.hide();
          } else {
            setIsSigningIn(false);
            LoadingIndicator.enable();
          }
        }
      );
    } else {
      setErrorStyle(false);
      LoadingIndicator.enable();
    }
  };

  const callbackTimer = () => {
    setShowBanner(false);
    setResendEnabled(true);
  };

  const resendTime = useCountdownNumber(resendTimer, callbackTimer); //time in seconds(number) for resend timer
  const callTime = useCountdownNumber(callMeTimer, () => setCallInsteadEnabled(true)); //time in seconds(number)for call me instead timer

  const resendButtonText = !resendEnabled
    ? `${_m('Resend Code')} in ${formatCountdown(t, resendTime * SECOND_TO_MS)}`
    : _m('Resend Code');

  const countdown = callInsteadEnabled ? '' : ` in ${formatCountdown(t, callTime * SECOND_TO_MS)}`;

  const onCallMeInsteadPressed = () => {
    if (callInsteadEnabled) {
      callMeInsteadPress();
      AWSManager.trackEvent(AWSConstants.ACCOUNT_SMS_VERIFY_CALL_SEND);
    }
  };

  useEffect(() => {
    if (resendEnabled) {
      setResendTimer(0);
    } else {
      setResendTimer(RESEND_COOL_DOWN);
    }
  }, [resendEnabled]);

  useEffect(() => {
    if (callInsteadEnabled) {
      setCallMeTimer(0);
    } else {
      setCallMeTimer(CALL_ME_COOL_DOWN);
    }
  }, [callInsteadEnabled]);

  return (
    <>
      {liveEvent ? <RegistrationStep step={1} /> : null}
      <div
        className={
          'w-[90%] max-w-[500px] relative text-white flex flex-col justify-center items-center bg-white bg-opacity-10 backdrop-blur-md shadow-lg rounded-lg mx-20 py-4'
        }
      >
        <p className={'px-4 text-center'}>
          <Trans
            t={_m}
            i18nKey={'SMS_CODE_INPUT_BODY'}
            components={{
              body: <span />,
              phone: <strong className='whitespace-nowrap' />, // eslint-disable  react-native/no-inline-styles
              edit: (
                <button
                  className='ml-1 px-4 text-sky-300 bg-slate-800 rounded-sm'
                  onClick={editPress}
                />
              ),
            }}
            values={{phoneNumber: phoneNumber, countryCode: countryValue}}
          />
        </p>
        <div className={'w-[18em] my-4'}>
          <OtpInputs
            useNewInputSizing
            colorVariant='dark'
            numberOfInputs={NUMBER_OF_INPUTS}
            handleChange={handleChange}
            autofillFromClipboard={true}
            autofillListenerIntervalMS={2000}
            errorStyle={errorStyle}
          />
        </div>
        {errorStyle && errorMessage ? (
          <div className={'w-[18em] -mt-2'}>
            <p className={'text-xs leading-4 text-red-500 font-light flex flex-row gap-3'}>
              <img src={IconInfo.src} alt={_m('Error')} className='error-icon' />
              {errorMessage}
            </p>
          </div>
        ) : null}
      </div>
      {gameIcon ? (
        <div
          className={
            'w-screen grow relative text-white flex flex-col justify-center items-center px-6'
          }
        >
          <Image
            src={gameIcon}
            className='rounded-md'
            alt={gameTitle || 'Skillz'}
            width={90}
            height={90}
            priority={true}
          />
        </div>
      ) : null}
      <div
        className={
          'w-screen grow relative text-white flex flex-col justify-center items-center px-6'
        }
      >
        <button
          className={`w-full max-w-[400px] bg-slate-700 p-3 rounded-xl ${
            callInsteadEnabled ? 'text-white' : 'text-slate-400 cursor-default'
          } font-bold text-lg mb-2`}
          onClick={onCallMeInsteadPressed}
          disabled={!callInsteadEnabled}
        >
          {_m('Call Me Instead')}
          {countdown}
        </button>
        <button
          className={`w-full max-w-[400px] ${
            resendEnabled ? 'bg-[#308ee2] text-white' : 'bg-[#6788a5] text-slate-300'
          } p-3 rounded-xl font-bold text-lg`}
          onClick={onSendPressed}
          disabled={!resendEnabled}
        >
          {resendButtonText}
        </button>
      </div>
    </>
  );
}
